import { Building2, Plus, Search, X } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import {
  Drawer,
  Empty,
  ErrorMessage,
  Loading,
  PageHeader,
  Pagination,
  StatusBadge,
} from '../components';
import { SearchSelect } from '../SearchSelect';
import { usePermissions } from '../permissions';
import type { Company, Status } from '../types';

type CompanyList = { items: Company[]; total: number; page: number; pageSize: number };
export function CompaniesPage() {
  const can = usePermissions();
  const [data, setData] = useState<CompanyList>({ items: [], total: 0, page: 1, pageSize: 25 });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<Status | ''>('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<unknown>();
  const [formError, setFormError] = useState<unknown>();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), pageSize: '25', search });
      if (status) params.set('status', status);
      void api<CompanyList>(`/api/companies?${params}`, { signal: controller.signal })
        .then((result) => {
          setData(result);
          setError(undefined);
        })
        .catch((caught) => {
          if (!controller.signal.aborted) setError(caught);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [search, status, page, refresh]);
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setFormError(undefined);
    try {
      await api<Company>('/api/companies', {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify({
          name: form.get('name'),
          legalName: form.get('legalName') || null,
          domains: form.get('domain') ? [{ domain: form.get('domain'), isPrimary: true }] : [],
        }),
      });
      setShowForm(false);
      setPage(1);
      setRefresh((old) => old + 1);
    } catch (caught) {
      setFormError(caught);
    } finally {
      setSaving(false);
    }
  }
  return (
    <>
      <PageHeader
        title="Kunden"
        description="Unternehmen und ihre Kontakte, Standorte und Geräte verwalten."
        actions={
          can('core:companies:write') && (
            <button
              className="button"
              onClick={() => {
                setFormError(undefined);
                setShowForm(true);
              }}
            >
              <Plus size={16} />
              Kunde anlegen
            </button>
          )
        }
      />
      <div className="filter-bar">
        <label>
          <span className="label">Kunden suchen</span>
          <div className="relative">
            <Search className="absolute left-3 top-3 text-muted" size={16} />
            <input
              className="input w-full pl-9"
              placeholder="Name oder Domain…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </label>
        <label>
          <span className="label">Status</span>
          <SearchSelect
            className="input w-full"
            value={status}
            onValueChange={(value) => {
              setStatus(value as Status | '');
              setPage(1);
            }}
          >
            <option value="">Alle Status</option>
            <option value="ACTIVE">Aktiv</option>
            <option value="INACTIVE">Inaktiv</option>
            <option value="ARCHIVED">Archiviert</option>
          </SearchSelect>
        </label>
        {(search || status) && (
          <button
            className="button-secondary"
            onClick={() => {
              setSearch('');
              setStatus('');
              setPage(1);
            }}
          >
            <X size={14} />
            Zurücksetzen
          </button>
        )}
      </div>
      <ErrorMessage error={error} />
      <div className="panel overflow-x-auto" aria-busy={loading}>
        {loading ? (
          <Loading>Kunden werden geladen…</Loading>
        ) : error ? (
          <Empty>
            Kunden konnten nicht geladen werden.
            <button className="button-secondary" onClick={() => setRefresh((old) => old + 1)}>
              Erneut versuchen
            </button>
          </Empty>
        ) : data.items.length === 0 ? (
          <Empty>
            {search || status
              ? 'Keine Kunden für diese Filter. Passen Sie die Suche an.'
              : 'Noch keine Kunden angelegt.'}
          </Empty>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Kunde</th>
                <th>Domain</th>
                <th>Kontakte</th>
                <th>Standorte</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-slate-100 text-muted">
                        <Building2 size={17} />
                      </div>
                      <div>
                        <Link
                          className="font-semibold hover:underline"
                          to={`/companies/${item.id}`}
                        >
                          {item.name}
                        </Link>
                        {item.legalName && item.legalName !== item.name && (
                          <div className="text-xs text-muted">{item.legalName}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="text-muted">
                    {item.domains.find((domain) => domain.isPrimary)?.domain ?? '—'}
                  </td>
                  <td>{item._count.contacts}</td>
                  <td>{item._count.sites}</td>
                  <td>
                    <StatusBadge status={item.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Pagination
        page={page}
        pageSize={25}
        total={data.total}
        loading={loading || Boolean(error)}
        onPageChange={setPage}
      />
      {showForm && (
        <Drawer
          title="Kunde anlegen"
          description="Der Name genügt. Weitere Angaben können Sie später ergänzen."
          onClose={() => {
            if (!saving) setShowForm(false);
          }}
        >
          <ErrorMessage error={formError} />
          <form onSubmit={create} className="space-y-5">
            <label className="block">
              <span className="label">Name *</span>
              <input name="name" required maxLength={200} className="input w-full" autoFocus />
            </label>
            <label className="block">
              <span className="label">Rechtlicher Name</span>
              <input name="legalName" className="input w-full" />
            </label>
            <label className="block">
              <span className="label">Primäre Domain</span>
              <input name="domain" className="input w-full" placeholder="example.de" />
              <span className="mt-1 block text-xs text-muted">Ohne https:// oder Pfad.</span>
            </label>
            <div className="flex gap-2">
              <button className="button" disabled={saving}>
                {saving ? 'Wird gespeichert…' : 'Kunde anlegen'}
              </button>
              <button
                type="button"
                className="button-secondary"
                disabled={saving}
                onClick={() => setShowForm(false)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </Drawer>
      )}
    </>
  );
}
